import {
  Box,
  Button,
  createStyles,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  makeStyles,
  TextField,
  Theme,
  Typography,
  useTheme,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { DataLoader } from "../components";
import { getVectorFields } from "../helper/collection.helper";
import { CollectionService } from "../services";
import {
  selectRecommendationSearchResults,
  selectSelectedCollection,
} from "../store/collection/collection.selector";
import RecommendationTable from "../module/recommendation/recommendation.table";
import { displayErrorMessage } from "../helper/redux.helpers";

interface IProps {
  open: boolean;
  onClose: () => void;
  multiSelect?: boolean;
  onAdd: (values: string[]) => void;
}

const style = makeStyles((theme: Theme) => {
  return createStyles({
    root: {
      [theme.breakpoints.up("md")]: {
        minWidth: 800,
      },
    },
  });
});

const RecommendationProductSearchModal: React.FC<IProps> = ({
  open,
  onClose,
  multiSelect,
  onAdd,
}) => {
  const theme = useTheme();
  const collection = useSelector(selectSelectedCollection);
  const classes = style();
  const [payload, setPayload] = useState("");
  const [vectorField, setVectorField] = useState<string>("");
  const recommendationResults = useSelector(selectRecommendationSearchResults);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const toggleSelectedsIds = (id: string) => {
    const index = selectedIds.indexOf(id);
    if (index !== -1) {
      selectedIds.splice(index, 1);
      setSelectedIds([...selectedIds]);
    } else {
      selectedIds.push(id);
      setSelectedIds([...selectedIds]);
    }
  };

  const handleAdd = () => {
    if (!multiSelect && selectedIds.length > 1) {
      displayErrorMessage("You can only select one document id");
    } else {
      onAdd(selectedIds);
      onClose();
    }
  };

  const handleSearch = async () => {
    CollectionService.searchTextDocuments(
      payload,
      vectorField,
      true,
      recommendationResults.data?.pageSize || 5,
      recommendationResults.data?.currentPage || 1
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      classes={{ paper: classes.root }}
    >
      <DialogTitle
        style={{
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.text.secondary,
        }}
      >
        Search product
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <Box display="flex" flexGrow={1} flexDirection="column">
              <Typography
                style={{ color: theme.palette.grey[500] }}
                variant="body2"
              >
                Payload
              </Typography>
              <TextField
                placeholder="Enter payload"
                variant="outlined"
                margin="dense"
                value={payload}
                onChange={(e) => setPayload(e.target.value)}
                fullWidth
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box display="flex" flexGrow={1} flexDirection="column">
              <Typography
                style={{ color: theme.palette.grey[500] }}
                variant="body2"
              >
                Search field
              </Typography>
              <Autocomplete
                fullWidth
                value={vectorField}
                onChange={(_, value) => setVectorField(value || "")}
                options={getVectorFields(collection.data?.schema || {})}
                renderInput={(params) => (
                  <TextField {...params} variant="outlined" margin="dense" />
                )}
              />
            </Box>
          </Grid>
        </Grid>

        <Box display="flex" justifyContent="flex-end">
          <Button
            color="primary"
            disableElevation
            size="large"
            style={{
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.common.white,
              textTransform: "none",
              marginTop: theme.spacing(),
              marginBottom: theme.spacing(),
            }}
            disabled={!payload}
            onClick={handleSearch}
          >
            Search
          </Button>
        </Box>
        {recommendationResults.initialized && (
          <React.Fragment>
            <Divider />

            <Box
              my={1}
              minHeight={300}
              display="flex"
              flexDirection="column"
              flexGrow={1}
            >
              <Typography>Results:</Typography>
              <DataLoader
                loading={recommendationResults.loading}
                empty={!(recommendationResults.data?.data || []).length}
                emptyText="No documents found"
              >
                <RecommendationTable
                  pagination={false}
                  currentPage={recommendationResults.data?.currentPage || 1}
                  data={recommendationResults.data?.data || []}
                  fields={recommendationResults.data?.fields || []}
                  handleNext={console.log}
                  handlePrev={console.log}
                  pageSize={recommendationResults.data?.pageSize || 5}
                  total={recommendationResults.data?.total || 0}
                  textColor="textPrimary"
                  selectable
                  multiSelect
                  onSelect={toggleSelectedsIds}
                />

                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  flexDirection="column"
                  my={2}
                >
                  {!multiSelect && selectedIds.length > 1 && (
                    <Typography color="error">
                      You can select only one document
                    </Typography>
                  )}
                  <Button
                    disableElevation
                    disableRipple
                    onClick={handleAdd}
                    style={{
                      textTransform: "none",
                      backgroundColor: theme.palette.primary.main,
                      marginTop: theme.spacing(),
                    }}
                    color="primary"
                    variant="contained"
                  >
                    Add
                  </Button>
                </Box>
              </DataLoader>
            </Box>
          </React.Fragment>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RecommendationProductSearchModal;
